/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type LocalVideoTrack,
  type Room,
  type TrackPublishDefaults,
  type VideoCaptureOptions,
  VideoPreset,
  VideoPresets,
} from "livekit-client";
import { BehaviorSubject } from "rxjs";

import {
  advancedCamera,
  cameraFrameRate,
  type CameraFrameRate,
  cameraResolution,
  type CameraResolution,
} from "../settings/settings";

export interface CameraPreferences {
  resolution: CameraResolution;
  frameRate: CameraFrameRate;
}

// Advanced camera options are snapshotted when a room is created, as upstream
// specifies. Later settings changes must not switch an active call's mode.
const cameraPresetModes = new WeakMap<Room, boolean>();

export function registerCameraPresetMode(room: Room, enabled: boolean): Room {
  cameraPresetModes.set(room, enabled);
  return room;
}

export function cameraPresetsEnabledForRoom(room: Room): boolean {
  return cameraPresetModes.get(room) ?? !advancedCamera.getValue();
}

export interface CameraSourceSettings {
  width?: number;
  height?: number;
  frameRate?: number;
}

export type CameraCaptureStatus =
  | {
      kind: "active";
      requested: CameraPreferences;
      actual: CameraSourceSettings;
    }
  | {
      kind: "error";
      requested: CameraPreferences;
      errorName: string;
    };

export interface CameraVideoOptions {
  capture: VideoCaptureOptions;
  publish: Pick<
    TrackPublishDefaults,
    "videoEncoding" | "backupCodec" | "videoSimulcastLayers"
  >;
}

const presets: Record<CameraResolution, VideoPreset> = {
  720: VideoPresets.h720,
  1080: VideoPresets.h1080,
  1440: VideoPresets.h1440,
};

export function getCameraTarget(resolution: CameraResolution): {
  width: number;
  height: number;
} {
  const preset = presets[resolution];
  return { width: preset.width, height: preset.height };
}

export const cameraCaptureStatus$ =
  new BehaviorSubject<CameraCaptureStatus | null>(null);

export function clearActiveCameraCaptureStatus(): void {
  if (cameraCaptureStatus$.value?.kind === "active") {
    cameraCaptureStatus$.next(null);
  }
}

export function getCameraPreferences(): CameraPreferences {
  return {
    resolution: cameraResolution.getValue(),
    frameRate: cameraFrameRate.getValue(),
  };
}

export function cameraPreferencesEqual(
  left: CameraPreferences,
  right: CameraPreferences,
): boolean {
  return (
    left.resolution === right.resolution && left.frameRate === right.frameRate
  );
}

function presetAtFrameRate(
  preset: VideoPreset,
  frameRate: CameraFrameRate,
): VideoPreset {
  return new VideoPreset({
    width: preset.width,
    height: preset.height,
    aspectRatio: preset.aspectRatio,
    maxBitrate: bitrateAtFrameRate(preset.encoding.maxBitrate, frameRate),
    maxFramerate: frameRate,
    priority: preset.encoding.priority,
  });
}

function bitrateAtFrameRate(
  thirtyFpsBitrate: number,
  frameRate: CameraFrameRate,
): number {
  // LiveKit's presets are budgeted for 30 fps. Preserve that quality budget at
  // 24/30 fps and double it for 60 fps, including every simulcast layer. A
  // 1440p60 selection therefore has a 10 Mbps ceiling rather than being
  // squeezed into the 1440p30 5 Mbps ceiling.
  return Math.round(thirtyFpsBitrate * Math.max(1, frameRate / 30));
}

export function getCameraVideoOptions(
  preferences: CameraPreferences = getCameraPreferences(),
): CameraVideoOptions {
  const preset = presets[preferences.resolution];
  const videoEncoding = {
    ...preset.encoding,
    maxBitrate: bitrateAtFrameRate(
      preset.encoding.maxBitrate,
      preferences.frameRate,
    ),
    maxFramerate: preferences.frameRate,
  };

  return {
    capture: {
      resolution: {
        ...preset.resolution,
        frameRate: preferences.frameRate,
      },
      // Keep this a preference rather than an exact constraint. Some browsers
      // leak a newly opened camera track when applyConstraints rejects during
      // a LiveKit restart. We inspect and show the actual source settings, so
      // an unsupported choice remains selected but is never claimed as active.
      frameRate: preferences.frameRate,
    },
    publish: {
      videoEncoding,
      backupCodec: { codec: "vp8", encoding: videoEncoding },
      videoSimulcastLayers: [
        presetAtFrameRate(VideoPresets.h180, preferences.frameRate),
        presetAtFrameRate(VideoPresets.h360, preferences.frameRate),
      ],
    },
  };
}

export function cameraSourceMeetsPreferences(
  actual: CameraSourceSettings,
  requested: CameraPreferences,
): boolean {
  const preset = getCameraTarget(requested.resolution);
  const dimensionsMeetRequest =
    actual.width !== undefined &&
    actual.width >= preset.width &&
    actual.height !== undefined &&
    actual.height >= preset.height;
  // Cameras commonly report 29.97/59.94 for nominal 30/60 fps.
  const frameRateMeetsRequest =
    actual.frameRate !== undefined &&
    actual.frameRate + 0.5 >= requested.frameRate;
  return dimensionsMeetRequest && frameRateMeetsRequest;
}

export function reportCameraCaptureActive(
  track: Pick<LocalVideoTrack, "getSourceTrackSettings">,
  requested: CameraPreferences,
): void {
  if (!cameraPreferencesEqual(requested, getCameraPreferences())) return;
  // A processor such as background blur can replace mediaStreamTrack. Report
  // the camera source rather than the dimensions of the processed output.
  const { width, height, frameRate } = track.getSourceTrackSettings();
  cameraCaptureStatus$.next({
    kind: "active",
    requested,
    actual: { width, height, frameRate },
  });
}

export function reportCameraCaptureError(
  error: unknown,
  requested: CameraPreferences,
): void {
  if (!cameraPreferencesEqual(requested, getCameraPreferences())) return;
  const errorName =
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    typeof error.name === "string"
      ? error.name
      : "Error";
  cameraCaptureStatus$.next({
    kind: "error",
    requested,
    errorName,
  });
}
